// src/pages/orders/OrderBuilder.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  List,
  ListItemButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  Paper,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import DeleteIcon from "@mui/icons-material/Delete";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import SendIcon from "@mui/icons-material/Send";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";

export default function OrderBuilder() {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const getId = (v) => (typeof v === "string" ? v : v?._id || "");

  // ─── MASTERS & HIERARCHY ─────────────────────────────────────
  const [storehouses, setStorehouses] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [quadrants, setQuadrants] = useState([]);
  const [filters, setFilters] = useState([]);

  // ─── SELECTION STATE ─────────────────────────────────────────
  const [selStorehouse, setSelStorehouse] = useState("");
  const [selTab, setSelTab] = useState("");
  const [selItem, setSelItem] = useState(null);

  // ─── PER-QUADRANT FILTER & ITEMS ─────────────────────────────
  const [filterByQuad, setFilterByQuad] = useState({});
  const [itemsByQuad, setItemsByQuad] = useState({});

  // ─── CART ─────────────────────────────────────────────────────
  const [cart, setCart] = useState([]);
  const [sending, setSending] = useState(false);

  const fetchRef = useRef(0);

  // ─── INITIAL LOAD ────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      axios.get(`${process.env.REACT_APP_BASE_URL}common/storehouses`, { headers }),
      axios.get(`${process.env.REACT_APP_BASE_URL}common/jobs`, { headers }),
      axios.get(`${process.env.REACT_APP_BASE_URL}common/tabs`, { headers }),
      axios.get(`${process.env.REACT_APP_BASE_URL}common/quadrants`, { headers }),
      axios.get(`${process.env.REACT_APP_BASE_URL}common/filters`, { headers }),
    ])
      .then(([sh, jb, tb, qd, fl]) => {
        setStorehouses(sh.data);
        setJobs(jb.data);
        setTabs(tb.data);
        setQuadrants(qd.data);
        setFilters(fl.data);
        setSelStorehouse(getId(sh.data[0]?._id));
      })
      .catch(console.error);
  }, []);

  // ─── STOREHOUSE → TAB cascade ────────────────────────────────
  useEffect(() => {
    const first = tabs.find((t) => getId(t.storehouse) === selStorehouse);
    setSelTab(getId(first?._id) || "");
  }, [selStorehouse, tabs]);

  // ─── FETCH ITEMS PER QUADRANT ────────────────────────────────
  useEffect(() => {
    if (!selStorehouse || !selTab) return;
    const me = ++fetchRef.current;
    setTimeout(() => {
      if (fetchRef.current !== me) return;
      const quads = quadrants.filter((q) => getId(q.tab) === selTab);
      quads.forEach((q) => {
        const filterId = filterByQuad[q._id] || "";
        axios
          .get(`${process.env.REACT_APP_BASE_URL}common/items`, {
            headers,
            params: {
              storehouse: selStorehouse,
              tab: selTab,
              quadrant: q._id,
              ...(filterId && { filter: filterId }),
            },
          })
          .then((r) =>
            setItemsByQuad((prev) => ({ ...prev, [q._id]: r.data }))
          )
          .catch(console.error);
      });
    }, 100);
  }, [selStorehouse, selTab, filterByQuad, quadrants]);

  // ─── CART HELPERS ────────────────────────────────────────────
  const addLine = () => {
    if (!selItem) return alert("Select an item first");
    setCart((c) => [
      ...c,
      {
        job: "",
        notes: "",
        itemMaster: getId(selItem.itemMaster),
        locId: selItem.locId,
        description: selItem.itemName,
        quantity: 1,
        uom: "EA",
      },
    ]);
  };

  const updateLine = (i, k, v) =>
    setCart((c) => {
      const cp = [...c];
      cp[i][k] = v;
      return cp;
    });

  const sendOrder = async () => {
    if (!cart.length) return alert("Cart is empty");
    setSending(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}common/orders`,
        {
          orderNo: `ORD-${Date.now()}`,
          orderTime: new Date(),
          orderBy: localStorage.getItem("userId"),
          lines: cart,
        },
        { headers }
      );
      alert("Order created!");
      setCart([]);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create order");
    } finally {
      setSending(false);
    }
  };

  // ─── RENDER ───────────────────────────────────────────────────
  return (
    <PageLayout>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Build Your Order
      </Typography>
      <Grid container spacing={2}>
        {/* LEFT: Hierarchy & Items */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Select
            fullWidth
            value={selStorehouse}
            onChange={(e) => setSelStorehouse(e.target.value)}
            sx={{ mb: 3 }}
          >
            {storehouses.map((sh) => (
              <MenuItem key={sh._id} value={sh._id}>
                {sh.code}
                {sh.name && ` — ${sh.name}`}
              </MenuItem>
            ))}
          </Select>

          <Tabs
            value={selTab}
            onChange={(e, newValue) => setSelTab(newValue)}
            sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
          >
            {tabs
              .filter((t) => getId(t.storehouse) === selStorehouse)
              .map((t) => (
                <Tab key={t._id} label={t.name} value={t._id} />
              ))}
          </Tabs>

          <Grid container spacing={2}>
            {quadrants
              .filter((q) => getId(q.tab) === selTab)
              .map((q) => (
                <Grid key={q._id} size={{ xs: 12, md: 6 }}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {q.name}
                    </Typography>
                    <Box sx={{ display: "flex", flexGrow: 1 }}>
                      {/* Filters */}
                      <Box
                        sx={{
                          flex: "0 0 40%",
                          maxHeight: 350,
                          overflowY: "auto",
                          mr: 1,
                        }}
                      >
                        <List dense disablePadding>
                          <ListItemButton
                            selected={filterByQuad[q._id] === ""}
                            onClick={() =>
                              setFilterByQuad((f) => ({ ...f, [q._id]: "" }))
                            }
                            sx={{ fontSize: "0.85rem" }}
                          >
                            Complete
                          </ListItemButton>
                          {filters
                            .filter((f) => getId(f.quadrant) === getId(q._id))
                            .map((f) => (
                              <ListItemButton
                                key={f._id}
                                selected={filterByQuad[q._id] === f._id}
                                onClick={() =>
                                  setFilterByQuad((prev) => ({
                                    ...prev,
                                    [q._id]: f._id,
                                  }))
                                }
                                sx={{ fontSize: "0.85rem" }}
                              >
                                {f.name}
                              </ListItemButton>
                            ))}
                        </List>
                      </Box>

                      {/* Items */}
                      <Box
                        sx={{
                          flex: "1 1 auto",
                          maxHeight: 350,
                          overflowY: "auto",
                        }}
                      >
                        <List dense disablePadding>
                          {(itemsByQuad[q._id] || []).map((it) => (
                            <ListItemButton
                              key={it._id}
                              selected={getId(it._id) === getId(selItem?._id)}
                              onClick={() => setSelItem(it)}
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {it.itemName}
                            </ListItemButton>
                          ))}
                        </List>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
          </Grid>

          <Box sx={{ mt: 2, textAlign: "right" }}>
            <Button
              variant="contained"
              color="success"
              onClick={addLine}
              disabled={!selItem}
              startIcon={<AddShoppingCartIcon />}
            >
              Add to Cart
            </Button>
          </Box>
        </Grid>

        {/* RIGHT: Cart */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card elevation={3}>
            <CardHeader
              title="Cart"
              titleTypographyProps={{ variant: "h5" }}
              sx={{ py: 2 }}
            />
            <CardContent sx={{ p: 2 }}>
              <TableContainer sx={{ maxHeight: 350, mb: 3 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "2rem" }}>#</TableCell>
                      <TableCell sx={{ width: "6rem" }}>Job</TableCell>
                      <TableCell sx={{ width: "4rem" }}>Qty</TableCell>
                      <TableCell sx={{ width: "4rem" }}>UOM</TableCell>
                      <TableCell sx={{ width: "12rem", minWidth: "8rem" }}>
                        Desc
                      </TableCell>
                      <TableCell sx={{ width: "6rem", minWidth: "4rem" }}>
                        Notes
                      </TableCell>
                      <TableCell sx={{ width: "2rem" }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cart.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">
                            No items in cart
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      cart.map((line, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              value={line.job}
                              onChange={(e) =>
                                updateLine(idx, "job", e.target.value)
                              }
                              sx={{ fontSize: "0.7rem", minWidth: "6rem" }}
                            >
                              <MenuItem value="">— select —</MenuItem>
                              {jobs.map((j) => (
                                <MenuItem key={j._id} value={j._id}>
                                  {j.jobNumber}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={line.quantity}
                              onChange={(e) =>
                                updateLine(idx, "quantity", +e.target.value)
                              }
                              sx={{ width: "4rem" }}
                              inputProps={{ style: { fontSize: "0.7rem" } }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              multiline
                              value={line.uom}
                              onChange={(e) =>
                                updateLine(idx, "uom", e.target.value)
                              }
                              sx={{ width: "4rem" }}
                              inputProps={{ style: { fontSize: "0.7rem" } }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              fontSize: "0.7rem",
                            }}
                          >
                            {line.description}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              multiline
                              value={line.notes}
                              onChange={(e) =>
                                updateLine(idx, "notes", e.target.value)
                              }
                              sx={{ width: "6rem" }}
                              inputProps={{ style: { fontSize: "0.7rem" } }}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                setCart((c) => c.filter((_, i) => i !== idx))
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                onClick={sendOrder}
                disabled={!cart.length || sending}
                startIcon={
                  sending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SendIcon />
                  )
                }
              >
                {sending ? "Creating…" : "Send Order"}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageLayout>
  );
}
